
import { defineComponent, onMounted, ref } from "vue";
import * as d3 from "d3";
import * as topojson from "topojson-client";
import { Topology, GeometryCollection } from "topojson-specification";
import { GetLegendItems } from "@/utils";
import stateGeom from "@/data/states.json";
import greenSeries from "@/data/greenSeries";
import redSeries from "@/data/redSeries";

export default defineComponent({
  props: ["metricData", "dataProp", "color"],
  setup(props) {
    const path = d3.geoPath();
    const width = 975;
    const height = 610;
    const mapContainer = ref();
    const mapRef: any = ref(null);
    let showPopup = ref(false);
    let showClose = ref(false);
    let popupState = ref("");
    let popupRate = ref("");
    let style = ref();
    let states: any;
    let svg: any;
    let zoom: any;
    let g: any;
    const seriesColors = props.color === "red" ? redSeries : greenSeries;
    const legend = GetLegendItems(
      seriesColors,
      props.metricData,
      props.dataProp
    );

    const closePopup = ()  => {
      showPopup.value = false;
      showClose.value = false;
    }

    const reset = () => {
      states.transition().style("fill", null);
      svg
        .transition()
        .duration(750)
        .call(
          zoom.transform,
          d3.zoomIdentity,
          d3.zoomTransform(svg.node()).invert([width / 2, height / 2])
        );
      showPopup.value = false;
    };

    const displayPopup = (event: any, d: any, container: any) => {
      const [[x0, y0], [x1, y1]] = path.bounds(d);
      const wasClicked = (event.type === "click")
      event.stopPropagation();
      const found = props.metricData.filter(
        (x: any) => x.state.toLowerCase() == d.properties.name.toLowerCase()
      );
      if (found.length > 0 && (wasClicked || (!showClose.value && !wasClicked))) {
        showPopup.value = true;

        showClose.value = ((showClose.value && !wasClicked) || wasClicked); // show when clicked or 
        popupRate.value = `${Number(found[0][props.dataProp]).toFixed(1)}%`;
        popupState.value = d.properties.name;
        const y = event.clientY;
        const x = event.clientX;

        const popStyle = {
          position: `fixed`,
          minWidth: `170px`,
          background: `#FFF`,
          border: `1px solid #D0D0D0`,
          borderRadius: `2px`,
          boxShadow: `0px 4px 8px rgba(0,0,0, 0.2)`,
          display: "flex",
          flexDirection: "column",
          padding: `0.5rem 2.25rem 0.5rem 0.75rem`,
          top: "",
          left: "",
          right: "",
        };
        const rect = container.getBoundingClientRect();
        const xOffset = x - rect.left;
        const yOffset = y - rect.top;

        if (xOffset > rect.width / 2) {
          popStyle.left = `${x - 180}px`;
        } else {
          popStyle.left = `${x + 10}px`;
        }

        if (yOffset > rect.height / 2) {
          popStyle.top = `${y - 80}px`;
        } else {
          popStyle.top = `${y + 10}px`;
        }

        style.value = popStyle;
      }
    };

    const zoomed = (event: any) => {
      const { transform } = event;
      g.attr("transform", transform);
      g.attr("stroke-width", 1 / transform.k);
      closePopup();
    };

    onMounted(() => {
      zoom = d3.zoom().scaleExtent([1, 8]).on("zoom", zoomed);

      svg = d3
        .select(mapRef.value)
        .attr("viewBox", `0, 0, ${width}, ${height}`)
        .on("click", reset);

      g = svg.append("g");

      states = g
        .append("g")
        .attr("cursor", "pointer")
        .selectAll("path")
        .data(
          topojson.feature(
            stateGeom as unknown as Topology,
            stateGeom.objects.states as GeometryCollection
          ).features
        )
        .join("path")
        .on("click", (event: any, d: any) => {
          displayPopup(event, d, mapContainer.value);
        })
        .on("mouseover", (event: any, d: any) => {
          displayPopup(event, d, mapContainer.value);
        })
        .on("mouseout", (event: any, d: any) => {
          if (!showClose.value) {
            closePopup();
          }
        })
        .attr("d", path)
        .attr("stroke", "#000")
        .attr("fill", (d: any) => {
          // Get the state's color
          const found = props.metricData.filter(
            (x: any) => x.state.toLowerCase() == d.properties.name.toLowerCase()
          );
          let color = "#444";
          if (found.length > 0) {
            const rate = found[0][props.dataProp];
            color = legend.filter((x: any) => {
              return rate >= x.min && rate <= x.max;
            })[0].iconCSS.backgroundColor;
          }
          return color;
        });

      svg.call(zoom);
    });

    return {
      mapRef,
      mapContainer,
      showPopup,
      showClose,
      popupState,
      popupRate,
      style,
      closePopup,
    };
  },
});
